class InfiniteScrollCollection extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.nextUrl = this.dataset.nextUrl || null;
    this.loading = false;
    this.grid = document.querySelector(`#product-grid[data-id="${this.sectionId}"]`);
    this.storageKey = `infiniteScroll:${window.location.pathname}${window.location.search}`;

    this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
      rootMargin: '600px 0px 600px 0px'
    });

    this.restoreIfNeeded();

    window.addEventListener('pagehide', () => this.saveScrollPosition());
  }

  isBackForwardNavigation() {
    const entries = performance.getEntriesByType('navigation');
    return entries.length > 0 && entries[0].type === 'back_forward';
  }

  saveState(pagesLoaded) {
    const existing = this.getSavedState();
    sessionStorage.setItem(
      this.storageKey,
      JSON.stringify({
        pagesLoaded: pagesLoaded,
        scrollY: existing ? existing.scrollY : 0
      })
    );
  }

  saveScrollPosition() {
    const existing = this.getSavedState();
    if (existing) {
      sessionStorage.setItem(
        this.storageKey,
        JSON.stringify({ pagesLoaded: existing.pagesLoaded, scrollY: window.scrollY })
      );
    }
  }

  getSavedState() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  restoreIfNeeded() {
    const saved = this.getSavedState();

    if (!saved || !this.isBackForwardNavigation() || saved.pagesLoaded < 1) {
      sessionStorage.removeItem(this.storageKey);
      this.observer.observe(this);
      return;
    }

    this.loading = true;
    this.classList.add('loading');
    this.restorePages(saved.pagesLoaded, saved.scrollY);
  }

  restorePages(remaining, scrollY) {
    if (remaining < 1 || !this.nextUrl) {
      this.loading = false;
      this.classList.remove('loading');
      this.observer.observe(this);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      });
      return;
    }

    this.fetchNextPage().then(() => {
      this.restorePages(remaining - 1, scrollY);
    });
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && this.nextUrl && !this.loading) {
        this.loading = true;
        this.classList.add('loading');
        this.fetchNextPage().then(() => {
          this.loading = false;
          this.classList.remove('loading');
        });
      }
    });
  }

  fetchNextPage() {
    const url = new URL(this.nextUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);

    return fetch(url.toString())
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const newGrid = html.querySelector(`#product-grid[data-id="${this.sectionId}"]`);
        const newItems = newGrid ? newGrid.querySelectorAll('.grid__item') : [];

        newItems.forEach((item) => {
          this.grid.appendChild(item);
        });

        const newSentinel = html.querySelector(`#InfiniteScrollSentinel-${this.sectionId}`);
        this.nextUrl = newSentinel ? newSentinel.dataset.nextUrl || null : null;

        const currentState = this.getSavedState();
        const pagesLoaded = currentState ? currentState.pagesLoaded + 1 : 1;
        this.saveState(pagesLoaded);

        if (!this.nextUrl) {
          this.observer.disconnect();
        }

        document.dispatchEvent(new CustomEvent('infinite-scroll:page-loaded'));
      })
      .catch((error) => {
        console.error('Infinite scroll error:', error);
      });
  }
}

customElements.define('infinite-scroll-collection', InfiniteScrollCollection);